/**
 * SettingsTab Component
 *
 * Settings tab for the profile page displaying user preferences and account actions.
 * Includes appearance settings (dark mode), grade display preferences, and account management.
 */

import { useState, useContext, type CSSProperties } from 'react';
import { useAuth } from '@/hooks/useAuth';
import { useGymMemberships } from '@/hooks/useGymMemberships';
import { ThemeContext } from '@/contexts/ThemeContext';
import { Toggle } from '@/components/ui/Toggle';
import { AccountDeleteDialog } from '@/components/auth/AccountDeleteDialog';
import CheckIcon from '@mui/icons-material/Check';

type GradePreference = 'v-scale' | 'font';

interface GradeOption {
  value: GradePreference;
  label: string;
  examples: string;
  description: string;
}

const GRADE_OPTIONS: GradeOption[] = [
  {
    value: 'v-scale',
    label: 'V-Scale',
    examples: 'VB, V0, V1, V2, V3...',
    description: 'American bouldering grades. Most common in the US and widely used worldwide.',
  },
  {
    value: 'font',
    label: 'Font Scale',
    examples: '4, 5, 5+, 6A, 6A+, 6B...',
    description:
      'French/Fontainebleau grades. Popular in Europe and often used for indoor climbing.',
  },
];

/**
 * SettingsTab component for user preferences and account management
 */
export const SettingsTab = () => {
  const { user, userProfile, signOut, updateGradeDisplayPreference } = useAuth();
  const { memberships } = useGymMemberships();
  const { theme, toggleTheme } = useContext(ThemeContext);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [isUpdatingPreference, setIsUpdatingPreference] = useState(false);

  const gradePreference = userProfile?.gradeDisplayPreference ?? 'v-scale';

  const handleGradePreferenceChange = async (newPreference: GradePreference) => {
    if (isUpdatingPreference || newPreference === gradePreference) return;
    setIsUpdatingPreference(true);
    try {
      await updateGradeDisplayPreference(newPreference);
    } catch (error) {
      console.error('Failed to update grade preference:', error);
    } finally {
      setIsUpdatingPreference(false);
    }
  };

  // Check if user owns any gyms (owners cannot delete their account)
  const ownedGyms = memberships.filter((m) => m.role === 'owner');
  const canDeleteAccount = ownedGyms.length === 0;

  /**
   * Format member since date
   */
  const memberSince = user?.metadata?.creationTime
    ? new Date(user.metadata.creationTime).toLocaleDateString('en-US', {
        month: 'short',
        year: 'numeric',
      })
    : 'Unknown';

  return (
    <div style={containerStyle}>
      {/* Appearance Section */}
      <section style={sectionStyle}>
        <h3 style={sectionTitleStyle}>Appearance</h3>
        <div style={settingRowStyle}>
          <span style={settingLabelStyle}>Dark Mode</span>
          <Toggle checked={theme === 'dark'} onChange={toggleTheme} />
        </div>
      </section>

      {/* Grade Display Section */}
      <section style={sectionStyle}>
        <h3 style={sectionTitleStyle}>Grade Display</h3>
        <p style={sectionDescStyle}>
          Select your preferred grading system. All climb difficulties will be shown in the format
          you choose.
        </p>
        <div style={gradeOptionsContainerStyle}>
          {GRADE_OPTIONS.map((option) => {
            const isSelected = gradePreference === option.value;
            return (
              <button
                key={option.value}
                style={{
                  ...gradeOptionStyle,
                  ...(isSelected ? gradeOptionSelectedStyle : {}),
                  ...(isUpdatingPreference ? gradeOptionDisabledStyle : {}),
                }}
                onClick={() => handleGradePreferenceChange(option.value)}
                disabled={isUpdatingPreference}
                aria-pressed={isSelected}
              >
                <div style={gradeOptionContentStyle}>
                  <span style={gradeOptionLabelStyle}>{option.label}</span>
                  <span style={gradeOptionExamplesStyle}>{option.examples}</span>
                </div>
                {isSelected && <CheckIcon style={gradeOptionCheckStyle} />}
              </button>
            );
          })}
        </div>
      </section>

      {/* Account Section */}
      <section style={sectionStyle}>
        <h3 style={sectionTitleStyle}>Account</h3>
        <div style={infoRowStyle}>
          <span style={infoLabelStyle}>Email</span>
          <span style={infoValueStyle}>{user?.email}</span>
        </div>
        <div style={infoRowStyle}>
          <span style={infoLabelStyle}>Member since</span>
          <span style={infoValueStyle}>{memberSince}</span>
        </div>
      </section>

      {/* Actions Section */}
      <div style={actionsStyle}>
        <button style={signOutButtonStyle} onClick={signOut}>
          Sign Out
        </button>
        <button
          style={{
            ...deleteButtonStyle,
            ...(canDeleteAccount ? {} : disabledDeleteButtonStyle),
          }}
          onClick={() => setShowDeleteDialog(true)}
          disabled={!canDeleteAccount}
        >
          Delete Account
        </button>
        {!canDeleteAccount && (
          <p style={deleteWarningStyle}>
            You own {ownedGyms.length} gym{ownedGyms.length > 1 ? 's' : ''}. Transfer ownership
            before deleting your account.
          </p>
        )}
      </div>

      {/* Account Delete Dialog */}
      <AccountDeleteDialog isOpen={showDeleteDialog} onClose={() => setShowDeleteDialog(false)} />
    </div>
  );
};

/**
 * Component styles using CSS variables for theming
 */
const containerStyle: CSSProperties = {
  padding: 'var(--spacing-md)',
};

const sectionStyle: CSSProperties = {
  marginBottom: 'var(--spacing-lg)',
};

const sectionTitleStyle: CSSProperties = {
  fontSize: 'var(--font-size-sm)',
  fontWeight: 600,
  color: 'var(--color-text-secondary)',
  textTransform: 'uppercase',
  marginBottom: 'var(--spacing-sm)',
};

const settingRowStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: 'var(--spacing-md)',
  backgroundColor: 'var(--color-bg-secondary)',
  borderRadius: 'var(--radius-md)',
  marginBottom: 'var(--spacing-sm)',
};

const settingLabelStyle: CSSProperties = {
  fontSize: 'var(--font-size-base)',
  color: 'var(--color-text-primary)',
};

const sectionDescStyle: CSSProperties = {
  fontSize: 'var(--font-size-sm)',
  color: 'var(--color-text-secondary)',
  marginBottom: 'var(--spacing-md)',
  lineHeight: 1.5,
};

const gradeOptionsContainerStyle: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: 'var(--spacing-xs)',
};

const gradeOptionStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: 'var(--spacing-sm)',
  padding: 'var(--spacing-sm) var(--spacing-md)',
  backgroundColor: 'var(--color-bg-secondary)',
  border: '2px solid transparent',
  borderRadius: 'var(--radius-md)',
  cursor: 'pointer',
  textAlign: 'left',
  transition: 'border-color 0.2s, background-color 0.2s',
};

const gradeOptionSelectedStyle: CSSProperties = {
  borderColor: 'var(--color-primary)',
  backgroundColor: 'var(--color-bg-tertiary)',
};

const gradeOptionDisabledStyle: CSSProperties = {
  opacity: 0.6,
  cursor: 'wait',
};

const gradeOptionContentStyle: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: '2px',
  flex: 1,
};

const gradeOptionLabelStyle: CSSProperties = {
  fontSize: 'var(--font-size-sm)',
  fontWeight: 600,
  color: 'var(--color-text-primary)',
};

const gradeOptionCheckStyle: CSSProperties = {
  fontSize: 18,
  color: 'var(--color-primary)',
  flexShrink: 0,
};

const gradeOptionExamplesStyle: CSSProperties = {
  fontSize: 'var(--font-size-xs)',
  color: 'var(--color-text-secondary)',
};

const infoRowStyle: CSSProperties = {
  display: 'flex',
  justifyContent: 'space-between',
  padding: 'var(--spacing-sm) 0',
  borderBottom: '1px solid var(--color-border)',
};

const infoLabelStyle: CSSProperties = {
  color: 'var(--color-text-secondary)',
};

const infoValueStyle: CSSProperties = {
  color: 'var(--color-text-primary)',
};

const actionsStyle: CSSProperties = {
  marginTop: 'var(--spacing-xl)',
};

const signOutButtonStyle: CSSProperties = {
  width: '100%',
  padding: 'var(--spacing-md)',
  fontSize: 'var(--font-size-base)',
  backgroundColor: 'var(--color-bg-secondary)',
  border: '1px solid var(--color-border)',
  borderRadius: 'var(--radius-md)',
  cursor: 'pointer',
  marginBottom: 'var(--spacing-sm)',
};

const deleteButtonStyle: CSSProperties = {
  width: '100%',
  padding: 'var(--spacing-md)',
  fontSize: 'var(--font-size-base)',
  backgroundColor: 'transparent',
  color: 'var(--color-error)',
  border: '1px solid var(--color-error)',
  borderRadius: 'var(--radius-md)',
  cursor: 'pointer',
};

const disabledDeleteButtonStyle: CSSProperties = {
  opacity: 0.5,
  cursor: 'not-allowed',
};

const deleteWarningStyle: CSSProperties = {
  marginTop: 'var(--spacing-sm)',
  fontSize: 'var(--font-size-sm)',
  color: 'var(--color-text-secondary)',
  textAlign: 'center',
};
